import {z} from "zod";

export const AttachmentsSchema = z.object({
    patient_card_id: z.number(),
    images: z
        .array(z.instanceof(File))
        .nonempty({ message: "At least one file must be attached" }),
});

export const DoctorCreateSchema = z.object({
    color: z.string().optional(),
    name: z.string().min(1, "Ime je obavezno"),
    password: z.string().min(8, "Šifra mora imati najmanje 8 karaktera"),
    password_confirmation: z.string().min(8, "Molimo potvrdite šifru"),
    phone_number: z.string().optional(),
    username: z.string().min(1, "Korisničko ime je obavezno"),
}).refine((data) => data.password === data.password_confirmation, {
    message: "Šifre se moraju poklapati",
    path: ["password_confirmation"],
});

export type DoctorFormPayload = z.infer<typeof DoctorCreateSchema>;

export const DoctorUpdateSchema = z.object({
    color: z.string().optional(),
    name: z.string().min(1, "Ime je obavezno"),
    phone_number: z.string().optional(),
    username: z.string().min(1, "Korisničko ime je obavezno"),
});

export type DoctorEditFormPayload = z.infer<typeof DoctorUpdateSchema>;

export const EventSchema = z.object({
    description: z.string().optional(),
    end_at: z.string().min(1, "Datum kraja je obavezan"),
    name: z.string().min(1, "Ime je obavezno"),
    start_at: z.string().min(1, "Datum početka je obavezan"),
    event_type: z.string().min(1, "Tip pregleda je obavezan"),
    doctor_id: z.string().min(1, "Doktor je obavezan"),
    history_entry: z
        .object({
            appointment_at: z.string().min(1, "Datum pregleda je obavezan"),
            bill: z.number().nonnegative().optional(),
            event_id: z.number().optional(),
            paid: z.boolean().optional(),
            patient_card_id: z.number(),
            tooth: z.string().optional(),
            work_done: z.string().optional(),
        })
        .optional(),
    patient_id: z.number("Pacijent je obavezan"
    )});

export type EventFormPayload = z.infer<typeof EventSchema>;

export const EventUpdateSchema = z.object({
    description: z.string().optional(),
    end_at: z.string().min(1, "Datum kraja je obavezan"),
    name: z.string().min(1, "Ime je obavezno"),
    start_at: z.string().min(1, "Datum početka je obavezan"),
    event_type: z.string().min(1, "Tip pregleda je obavezan"),
    doctor_id: z.string().min(1, "Doktor je obavezan"),
    history_entry_attributes: z
        .object({
            _destroy: z.boolean().optional(),
            appointment_at: z.string().optional(),
            bill: z.number().nonnegative().optional(),
            event_id: z.number().optional(),
            paid: z.boolean().optional(),
            patient_card_id: z.number().optional(),
            tooth: z.string().max(255).optional(),
            work_done: z.string().max(255).optional(),
            id: z.number().optional(),
        })
        .optional(),
    patient_id: z.number("Pacijent je obavezan"
    )});

export type EventEditFormPayload = z.infer<typeof EventUpdateSchema>;

export const HistoryEntrySchema = z.object({
    appointment_at: z.string().min(1, "Datum pregleda je obavezan"),
    bill: z.number().nonnegative().optional(),
    paid: z.boolean().optional(),
    tooth: z.string().optional(),
    work_done: z.string().optional(),
    event_id: z.number().optional(),
    patient_card_id: z.number(),
});

export type HistoryEntryFormPayload = z.infer<typeof HistoryEntrySchema>;

export const HistoryEntryUpdateSchema = z.object({
    appointment_at: z.string(),
    bill: z.number().nonnegative().optional(),
    paid: z.boolean().optional(),
    tooth: z.string().max(255).optional(),
    work_done: z.string().max(255).optional(),
    event_id: z.number().optional(),
    patient_card_id: z.number(),
});

export type HistoryEntryEditFormPayload = z.infer<typeof HistoryEntryUpdateSchema>;

export const NurseCreateSchema = z.object({
    name: z.string().min(1, "Ime je obavezno"),
    password: z.string().min(8, "Šifra mora imati najmanje 8 karaktera"),
    password_confirmation: z.string().min(8, "Molimo potvrdite šifru"),
    phone_number: z.string().optional(),
    username: z.string().min(1, "Korisničko ime je obavezno"),
}).refine((data) => data.password === data.password_confirmation, {
    message: "Šifre se moraju poklapati",
    path: ["password_confirmation"],
});

export type NurseFormPayload = z.infer<typeof NurseCreateSchema>;

export const NurseUpdateSchema = z.object({
    name: z.string().min(1, "Ime je obavezno"),
    phone_number: z.string().optional(),
    username: z.string().min(1, "Korisničko ime je obavezno"),
});

export type NurseEditFormPayload = z.infer<typeof NurseUpdateSchema>;

export const OptometristSchema = z.object({
    name: z.string().min(1, "Ime je obavezno"),
    phone_number: z.string().optional(),
});

export type OptometristFormPayload = z.infer<typeof OptometristSchema>;

export const PatientSchema = z.object({
    address: z.string().optional(),
    call_before_appointment: z.boolean().optional(),
    city: z.string().optional(),
    name: z.string().min(1, "Ime je obavezno"),
    note: z.string().optional(),
    orthodontics: z.boolean().optional(),
    phone_number: z.string().optional(),
    xray: z.string().optional(),
    year_of_birth: z.number().int().optional(),
    doctor_id: z.string().min(1, "Doktor je obavezan"),
    orthodontist_id: z.number().optional(),
})
    .refine(
        (data) => !(data.orthodontics && !data.orthodontist_id),
        {
            message: "Ortodont je obavezan",
            path: ["orthodontist_id"],
        }
    );

export type PatientFormPayload = z.infer<typeof PatientSchema>;
